/**
 * Lightweight analytics layer for The Identity Atelier.
 *
 * Loads Google Analytics 4 (gtag) and Meta Pixel (fbq) on demand,
 * driven by env vars. Helpers no-op silently if the corresponding ID
 * isn't configured, so preview/dev never sends false data.
 *
 * Env vars (set in /app/frontend/.env at build time):
 *   REACT_APP_GA4_MEASUREMENT_ID = "G-XXXXXXXXXX"
 *   REACT_APP_META_PIXEL_ID      = "1234567890"
 */

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
const META_ID = process.env.REACT_APP_META_PIXEL_ID || "";

let _initialised = false;

const _loadGa4 = () => {
  if (!GA4_ID || typeof window === "undefined") return;
  if (window.gtag) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line func-names
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: false });
};

const _loadMetaPixel = () => {
  if (!META_ID || typeof window === "undefined") return;
  if (window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", META_ID);
};

export const initAnalytics = () => {
  if (_initialised) return;
  _initialised = true;
  _loadGa4();
  _loadMetaPixel();
};

const _ga = (...args) => {
  if (GA4_ID && typeof window !== "undefined" && window.gtag) {
    window.gtag(...args);
  }
};

const _fb = (action, name, data) => {
  if (META_ID && typeof window !== "undefined" && window.fbq) {
    if (data) window.fbq(action, name, data);
    else window.fbq(action, name);
  }
};

/* ---------- High-level helpers ---------- */

export const trackPageView = (path) => {
  _ga("event", "page_view", { page_path: path });
  _fb("track", "PageView");
};

export const trackViewItem = (product) => {
  if (!product) return;
  _ga("event", "view_item", {
    currency: "GBP",
    value: product.price,
    items: [{ item_id: product.id, item_name: product.name, item_category: product.category, price: product.price }],
  });
  _fb("track", "ViewContent", {
    content_ids: [product.id],
    content_name: product.name,
    content_type: "product",
    content_category: product.category,
    value: product.price,
    currency: "GBP",
  });
};

export const trackAddToCart = (product, { size, color, quantity }) => {
  if (!product) return;
  _ga("event", "add_to_cart", {
    currency: "GBP",
    value: product.price * quantity,
    items: [{ item_id: product.id, item_name: product.name, item_variant: `${color}/${size}`, quantity, price: product.price }],
  });
  _fb("track", "AddToCart", {
    content_ids: [product.id],
    content_name: product.name,
    content_type: "product",
    value: product.price * quantity,
    currency: "GBP",
  });
};

export const trackBeginCheckout = ({ value, items }) => {
  _ga("event", "begin_checkout", { currency: "GBP", value, items });
  _fb("track", "InitiateCheckout", {
    content_ids: items.map((i) => i.item_id),
    contents: items.map((i) => ({ id: i.item_id, quantity: i.quantity })),
    num_items: items.reduce((s, i) => s + i.quantity, 0),
    value,
    currency: "GBP",
  });
};

export const trackPurchase = ({ transaction_id, value }) => {
  _ga("event", "purchase", { transaction_id, currency: "GBP", value });
  _fb("track", "Purchase", { value, currency: "GBP" });
};
